import React, { useEffect, useRef, useState } from 'react';
import { useGate } from 'effector-react';
import { delay } from 'pano-utils';
import type { Subscription } from 'effector';
import useToggle from '@hooks/useToggle';
import {
  $GateRotateEditMode,
  $GateRotateItemEditing,
  $rotateEditApi,
} from '@stores/autoRotateStores';
import Button from '@components/Button';
import { $loadedScene } from '@stores/loadedStore';
import { MainPlayer } from '@stores/krpPlayerStores';
import { showConfirmModal } from '@components/ConfirmModal';
import {
  addRotateItem,
  krpAddCustomRotate,
  krpRemoveCustomRotate,
} from '../actions/krpRotate';
import { PanelRotateItem } from '../PanelRotateItem';
import { onRotateItemClick } from '../actions/onRotateItemClick';
import {
  startCustomRotate,
  stopCustomRotate,
} from '../actions/customAutoRotate';
import { onUserInteractive } from '../../player/actions/onUserInteractive';
import { rotateToPromise } from '../utils/rotateLookTo';

export function CustomRotate() {
  const editMode = useToggle(false);
  useGate($GateRotateEditMode, editMode.isOn);
  const [editingId, setEditingId] = useState<string | null>(null);
  const autoStartTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const unwatch = onRotateItemClick.watch(async (id) => {
      const editing = $GateRotateItemEditing.state.getState();
      if (editing?.id === id) return;
      if (editing?.isChanged) {
        const { confirmed } = await showConfirmModal({
          title: '有未保存的更改',
          content: '当前所有未经保存的改动将丢失, 是否继续?',
        });
        if (!confirmed) return;
      }
      setEditingId(null);
      await delay(100);
      setEditingId(id);
    });

    return () => {
      unwatch();
      krpRemoveCustomRotate();
      MainPlayer.get()?.removeEventListener(
        'onPlayerDoubleClick',
        addRotateItem
      );
      $rotateEditApi.reset();
      MainPlayer.get()?.stopLookTo();
    };
  }, []);

  useEffect(() => {
    let unwatch: Subscription | undefined = undefined;
    const fn = () => {
      clearTimeout(autoStartTimer.current!);
      if (!editMode.isOn) {
        autoStartTimer.current = setTimeout(() => {
          const view = $loadedScene.getState()?.view;
          if (!view) return;
          startCustomRotate();
          unwatch?.();
        }, 3000);
      }
    };
    unwatch = onUserInteractive.watch(() => {
      fn();
    });
    fn();
    return () => {
      clearInterval(autoStartTimer.current!);
      stopCustomRotate();
      unwatch?.();
    };
  }, [editMode.isOn]);

  const handleStartEdit = async () => {
    krpAddCustomRotate();
    const { hLookAt, vLookAt, fov } = $loadedScene.getState()?.view ?? {};
    await rotateToPromise({ hlookat: hLookAt, vlookat: vLookAt, fov });
    editMode.on();
    MainPlayer.get()?.addEventListener('onPlayerDoubleClick', addRotateItem);
  };

  const handleEndEdit = () => {
    krpRemoveCustomRotate();
    editMode.off();
    MainPlayer.get()?.removeEventListener('onPlayerDoubleClick', addRotateItem);
  };

  return (
    <>
      <Button
        className="w-full"
        scheme={editMode.isOn ? 'orange' : 'blue'}
        onClick={editMode.isOn ? handleEndEdit : handleStartEdit}
      >
        {editMode.isOn ? '结束编辑' : '编辑导览'}
      </Button>
      {editingId !== null && (
        <PanelRotateItem
          id={editingId}
          onRequestClose={() => setEditingId(null)}
        />
      )}
    </>
  );
}
